import {FidjOidcClient} from '../identity/FidjOidcClient';
import * as tools from '../tools';
import * as connection from '../connection';
import {Ajax, ClientTokens, OwnerUser} from '../connection';
import * as session from '../session';
import {SessionCryptoInterface} from '../session';
import {
    EndpointCallInterface,
    EndpointFilterInterface,
    EndpointInterface,
    ErrorInterface,
    LoggerInterface,
    LoggerLevelEnum,
    ModuleServiceInitOptionsInterface,
    ModuleServiceLoginCallOptionsInterface,
    ModuleServiceLoginOptionsInterface,
    SdkInterface,
} from './Interfaces';
import {LoggerService} from './LoggerService';
import {FidjError} from './FidjError';
import {IService} from './IService';
import {bpInfo} from '../bpInfo';
import {verifyAppSession} from '../server/verifyAppSession';
import {
    FidjApiConsentsResponse,
    FidjApiConsentsUpdateRequest,
    FidjApiConsentsHistoryResponse,
    FidjApiUsersMeResponse,
    FidjApiUsersMeDetailsResponse,
    FidjApiUsersMeUpdateRequest,
    FidjApiResetPasswordRequest,
    FidjApiVerifyEmailRequest,
} from '@ofidj/contracts';

// TODO const PouchDB = window['PouchDB'] || require('pouchdb').default;

function refusalReason(err: any): string {
    const body = err?.message;
    if (typeof body === 'string' && body) {
        return body;
    }
    if (body && typeof body === 'object') {
        for (const field of ['status', 'reason', 'code', 'message']) {
            const value = body[field];
            if (typeof value === 'string' && value) {
                return value;
            }
        }
        try {
            return JSON.stringify(body);
        } catch {
            // Not serialisable; fall through to the error's own words.
        }
    }
    return err?.toString ? err.toString() : String(err);
}

export class FidjNodeService implements IService {
    public static DEFAULT_TIMEOUT_MS = 60000;
    public static SANDBOX_FIDJ_ID = 'fidj-sandbox-0123fe7ed0000001';

    private static _srvDataUniqId = 0;
    private sdk: SdkInterface;
    private logger: LoggerInterface;
    private promise: PromiseConstructor;
    private storage: tools.LocalStorage;
    private session: session.Session;
    private connection: connection.Connection;

    constructor(
        logger?: LoggerInterface,
        promise?: PromiseConstructor,
        options?: ModuleServiceInitOptionsInterface
    ) {
        this.sdk = {
            org: 'fidj',
            version: bpInfo.version.substring(1),
            prod: false,
            useDB: true,
        };
        if (promise) {
            this.promise = promise;
        } else {
            this.promise = Promise;
        }
        if (logger) {
            this.logger = logger;
        } else {
            this.logger = new LoggerService();
        }
        if (options && options.logLevel) {
            this.logger.setLevel(options.logLevel);
        }

        this.logger.log('fidj.sdk.service : constructor');

        this.storage = new tools.LocalStorage('fidj.');
        this.session = new session.Session();
        this.connection = new connection.Connection(this.sdk, this.storage, this.logger);
    }

    static IsJWTExpired(payload: any): boolean {
        if (!payload.exp) {
            return true;
        }

        const now = new Date();
        const expDate = new Date(payload.exp * 1000);
        return expDate.getTime() < now.getTime();
    }

    static NameFromPayload(payload: any): string {
        return payload?.name;
    }

    static RolesFromPayload(payload: any): string[] {
        let roles = [];
        try {
            roles = [].concat(payload.roles);
        } catch (ignored) {
            // If payload.roles is not an array or doesn't exist, keep roles as an empty array
        }

        return roles;
    }

    static AppIdFromPayload(payload: any): string {
        return payload?.aud;
    }

    public async init(fidjId?: string, options?: ModuleServiceInitOptionsInterface) {
        if (options?.logLevel) {
            this.logger.setLevel(options.logLevel);
        } else {
            this.logger.setLevel(LoggerLevelEnum.NONE);
        }

        this.logger.log('fidj.sdk.service.init : ', options);

        // Zero-config: no fidjId and no options → sandbox auto-detect
        const isZeroConfig = !fidjId && !options;
        if (isZeroConfig) {
            fidjId = FidjNodeService.SANDBOX_FIDJ_ID;
            options = {prod: false};
            this.logger.log('fidj.sdk.service.init : zero-config mode, using sandbox');
        }

        if (!fidjId) {
            this.logger.error('fidj.sdk.service.init : bad init');
            throw new FidjError(400, 'Need a fidjId');
        }

        this.sdk.prod = !options ? true : (options.prod ?? true);
        this.sdk.useDB = !options ? false : (options.useDB ?? false);
        await this.connection.init(
            this.sdk.version,
            fidjId,
            !options || !Object.prototype.hasOwnProperty.call(options, 'crypto')
                ? false
                : options.crypto,
            options?.apiEndpoint
        );

        let bestUrls = [],
            bestOldUrls = [];
        try {
            await this.connection.verifyConnectionStates();
            bestUrls = await this.connection.getApiEndpoints({filter: 'theBestOne'});
            bestOldUrls = await this.connection.getApiEndpoints({filter: 'theBestOldOne'});
        } catch (err) {
            this.logger.error('fidj.sdk.service.init: ', err);
            const cause = err instanceof Error ? err.message : err.toString();
            throw new FidjError(500, 'Could not verify API endpoints: ' + cause);
        }

        if (bestUrls.length === 0 && bestOldUrls.length === 0) {
            const envHint = this.sdk.prod
                ? 'https://api.fidj.ovh/v3'
                : 'https://api.sandbox.fidj.ovh/v3';
            throw new FidjError(
                404,
                'No reachable API endpoint. Tried: ' +
                    envHint +
                    '. Check your network, fidjId, and prod/sandbox setting.'
            );
        }

        const theBestFirstUrl = bestUrls.length ? bestUrls[0] : bestOldUrls[0];
        const isLogin = this.isLoggedIn();
        this.logger.log(
            'fidj.sdk.service.init > verifyConnectionStates : ',
            theBestFirstUrl,
            isLogin
        );

        this.connection.setClient(
            new connection.Client(
                this.connection.fidjId,
                theBestFirstUrl.url,
                this.storage,
                this.sdk,
                this.logger
            )
        );
    }

    public async login(
        login: string,
        password: string,
        options?: ModuleServiceLoginCallOptionsInterface
    ) {
        this.logger.log('fidj.sdk.service.login');
        if (!this.connection.isReady()) {
            throw new FidjError(404, 'Need an initialized FidjService');
        }
        this.forgetProviderSession();

        try {
            await this._removeAll();
            await this._createSession(this.connection.fidjId);
            const clientTokens = await this._loginInternal(login, password, undefined, options);
            await this.connection.setConnection(clientTokens);
        } catch (err: any) {
            if (err instanceof FidjError) {
                throw err;
            }
            const code = typeof err?.code === 'number' ? err.code : 500;
            // Ajax puts the API's response body in `message`. Keep it whole:
            // rebuilding a FidjError from `err.toString()` turned every refusal
            // that carried a body into "[object Object]", which cost the caller
            // the agreement it was being asked for and broke every message
            // keyed on the reason.
            const body = err?.message;
            throw new FidjError(
                code,
                refusalReason(err),
                body && typeof body === 'object' ? body : undefined
            );
        }

        if (!this.sdk.useDB) {
            return this.connection.getUser();
        }

        try {
            await this.session.sync(this.connection.getClientId());
        } catch (e) {
            this.logger.warn('fidj.sdk.service.login: sync -not blocking- issue  ', e.toString());
        }
        return this.connection.getUser();
    }

    public async loginInDemoMode(
        options?: ModuleServiceLoginOptionsInterface
    ): Promise<any | ErrorInterface> {
        // generate one day tokens if not set
        if (!options || !options.accessToken) {
            const now = new Date();
            now.setDate(now.getDate() + 1);
            const tomorrow = now.getTime();
            const payload = tools.Base64.encode(
                JSON.stringify({
                    roles: [],
                    message: 'demo',
                    apis: [],
                    endpoints: [],
                    dbs: [],
                    exp: tomorrow,
                })
            );
            const jwtSign = tools.Base64.encode(JSON.stringify({}));
            const token = jwtSign + '.' + payload + '.' + jwtSign;
            options = {
                accessToken: token,
                idToken: token,
                refreshToken: token,
            };
        }

        return new this.promise((resolve, reject) => {
            this._removeAll()
                .then(() => {
                    return this._createSession(this.connection.fidjId);
                })
                .then(async () => {
                    await this.connection.setConnectionOffline(options);
                    resolve(this.connection.getUser());
                })
                .catch((err) => {
                    this.logger.error('fidj.sdk.service.loginInDemoMode error: ', err);
                    reject(err);
                });
        });
    }

    // Convenience: init + login in one call
    // Usage: await srv.initAndLogin('user@test.com', 'pwd')  → zero-config sandbox
    // Usage: await srv.initAndLogin('user@test.com', 'pwd', 'my-app')  → prod
    public async initAndLogin(
        login: string,
        password: string,
        fidjId?: string,
        options?: ModuleServiceInitOptionsInterface,
        loginOptions?: ModuleServiceLoginCallOptionsInterface
    ) {
        await this.init(fidjId, options);
        return this.login(login, password, loginOptions);
    }

    // Convenience: init + loginInDemoMode in one call
    // Usage: await srv.initDemo()  → sandbox with mock tokens, zero setup
    public async initDemo(fidjId?: string, options?: ModuleServiceInitOptionsInterface) {
        await this.init(fidjId, options);
        return this.loginInDemoMode();
    }

    private oidcClient: FidjOidcClient;
    // Typing a credential says this session is not the provider's. The Fidj door
    // leaves `fidj.oidc.<appId>.config` behind, `clear()` keeps it when the
    // provider session dies, and `oidc()` reads only that config — so the tab
    // stayed in provider mode for good. A sign-in then minted its three tokens
    // and never used them, because every call starts with `if (this.oidc())` and
    // found no session behind it: watched on fidj.ovh as "Sign in first" over a
    // network trace that was green from end to end.
    private forgetProviderSession() {
        const client = this.oidc();
        if (!client) {
            return;
        }
        client.clear();
        this.oidcClient = undefined;
        sessionStorage.removeItem('fidj.oidc.' + this.connection.fidjId + '.config');
    }

    private oidc() {
        if (this.oidcClient) {
            return this.oidcClient;
        }
        if (typeof sessionStorage === 'undefined' || !this.connection.fidjId) {
            return undefined;
        }
        const saved = sessionStorage.getItem('fidj.oidc.' + this.connection.fidjId + '.config');
        if (!saved) {
            return undefined;
        }
        const options = JSON.parse(saved);
        const expectedApi =
            this.connection.apiEndpoint ||
            (this.sdk.prod ? 'https://api.fidj.ovh/v3' : 'https://api.sandbox.fidj.ovh/v3');
        if (
            options.apiEndpoint.replace(/\/$/, '') !== expectedApi.replace(/\/$/, '') ||
            options.clientId !== this.connection.fidjId
        ) {
            return undefined;
        }
        this.oidcClient = new FidjOidcClient({...options, storage: sessionStorage});
        return this.oidcClient;
    }

    public isLoggedIn(): boolean {
        if (this.oidc()) {
            return this.oidc().hasSession();
        }
        return this.connection.isLogin();
    }

    public needsRefresh(): boolean {
        return !this.connection.isReady();
    }

    public isConnected() {
        return this.connection?.isConnected();
    }

    public getOwnerUser() {
        const clientUser = this.connection.getUser();
        if (clientUser?.roles.indexOf('Owner') > -1) {
            return new OwnerUser(
                this,
                this.connection,
                clientUser.id,
                clientUser.username,
                clientUser.roles
            );
        }
        return null;
    }

    public async fidjGetEndpoints(
        filter?: EndpointFilterInterface
    ): Promise<Array<EndpointInterface>> {
        if (!filter) {
            filter = {showBlocked: false};
        }
        const ap = await this.connection.getAccessPayload({endpoints: []});
        let endpoints = JSON.parse(ap).endpoints;
        if (!endpoints || !Array.isArray(endpoints)) {
            return [];
        }

        endpoints = endpoints.filter((endpoint: EndpointInterface) => {
            let ok = true;
            if (ok && filter.key) {
                ok = endpoint.key === filter.key;
            }
            if (ok && !filter.showBlocked) {
                ok = !endpoint.blocked;
            }
            return ok;
        });
        return endpoints;
    }

    public async fidjRoles(): Promise<Array<any>> {
        if (!this.isLoggedIn() || this.connection.getUser()?.id === 'demo') {
            return JSON.parse(await this.connection.getIdPayload({roles: []})).roles || [];
        }
        const endpoints = await this.connection.getApiEndpoints({filter: 'theBestOne'});
        if (endpoints.length !== 1) {
            throw new FidjError(503, 'No identity endpoint is available.');
        }
        const session = await verifyAppSession(await this.fidjGetIdToken(), {
            appId: this.connection.fidjId,
            apiEndpoint: endpoints[0].url,
        });
        return session.roles;
    }

    public async fidjMessage(): Promise<string> {
        return JSON.parse(await this.connection.getIdPayload({message: ''})).message;
    }

    // Signing out of Fidj itself, which an app's sign-out deliberately is not:
    // it ends the identity session this browser is recognised by, so the next
    // screen asks instead of walking the person back in. It answers with where
    // the provider finishes that, for a caller that can leave the page.
    public async logoutFromFidj(): Promise<string | void | ErrorInterface> {
        if (this.oidc()) {
            return this.oidc().logout({endProviderSession: true});
        }
        return this.logout(true);
    }

    public async logout(force?: boolean): Promise<void | ErrorInterface> {
        if (this.oidc()) {
            return void (await this.oidc().logout());
        }
        if (!this.connection.getClient() && !force) {
            return this._removeAll().then(() => {
                return this.session.create(this.connection.fidjId, true);
            });
        }

        return this.connection
            .logout()
            .then(() => {
                return this._removeAll();
            })
            .catch(() => {
                return this._removeAll();
            })
            .then(() => {
                return this.session.create(this.connection.fidjId, true);
            });
    }

    public async sync(
        options: {
            forceRefresh: boolean;
            fnInitFirstData?: (a: any) => Promise<any>;
            fnInitFirstData_Arg?: any;
        } = {forceRefresh: false}
    ): Promise<void | ErrorInterface> {
        if (this.oidc()) {
            await this.oidc().request('/me');
            return;
        }
        this.logger.log('fidj.sdk.service.sync');
        this.logger.log('fidj.sdk.service.sync: you ar not using DB - no sync available.');

        try {
            const clientTokens = await this.connection.refreshConnection(options.forceRefresh);
            if (clientTokens) {
                await this.connection.setConnection(clientTokens);
            }
            this.logger.log('fidj.sdk.service.sync refreshConnection done. ');
        } catch (err) {
            this.logger.warn('fidj.sdk.service.sync refreshConnection failed : ', err);
            // The session is gone — revoked, expired, or from before the
            // credentials changed. Closed here, and reported as a 401, the one
            // code that means "sign in again": a 403 means an action was
            // refused to somebody still signed in.
            if (err?.code === 401 || err?.code === 403 || err?.code === 410) {
                await this.logout(true);
            }
            throw new FidjError(401, 'not connected');
        }

        // if (!self.session.isReady()) {
        //    return self.promise.reject('fidj.sdk.service.sync : DB sync impossible. Did you login ?');
        // }

        if (!this.sdk.useDB) {
            return;
        }

        const firstSync = this.session.dbLastSync === null;

        try {
            await this._createSession(this.connection.fidjId);
            await this.session.sync(this.connection.getClientId());
            this.logger.log('fidj.sdk.service.sync resolved');
        } catch (err) {
            this.logger.warn('fidj.sdk.service.sync warn: ', err);
        }

        const isEmpty = await this.session.isEmpty();
        this.logger.log('fidj.sdk.service.sync isEmpty : ', isEmpty, firstSync);

        if (isEmpty && firstSync && options.fnInitFirstData) {
            try {
                const ret = await options.fnInitFirstData(options.fnInitFirstData_Arg);
                this.logger.log('fidj.sdk.service.sync fnInitFirstData resolved: ', ret);
            } catch (e) {
                this.logger.warn('fidj.sdk.service.sync fnInitFirstData error: ', e);
            }
        }

        this.session.dbLastSync = new Date().getTime();
        const result = await this.session.info();

        this.session.dbRecordCount = 0;
        if (result && result.doc_count) {
            this.session.dbRecordCount = result.doc_count;
        }
        this.logger.log('fidj.sdk.service.sync _dbRecordCount : ' + this.session.dbRecordCount);
    }

    public async fidjPutInDb(data: any): Promise<string | ErrorInterface> {
        this.logger.log('fidj.sdk.service.fidjPutInDb: ', data);
        if (!this.sdk.useDB) {
            this.logger.log(
                'fidj.sdk.service.fidjPutInDb: you are not using DB - no put available.'
            );
            return Promise.resolve('NA');
        }

        if (!this.connection.getClientId()) {
            return this.promise.reject(
                new FidjError(401, 'DB put impossible. Need a user logged in.')
            );
        }
        if (!this.session.isReady()) {
            return this.promise.reject(new FidjError(400, 'Need to be synchronised.'));
        }

        let _id: string;
        if (data && typeof data === 'object' && Object.keys(data).indexOf('_id')) {
            _id = data._id;
        }
        if (!_id) {
            _id = this._generateObjectUniqueId(this.connection.fidjId);
        }
        let crypto: SessionCryptoInterface;
        if (this.connection.fidjCrypto) {
            crypto = {
                obj: this.connection,
                method: 'encrypt',
            };
        }

        return this.session.put(
            data,
            _id,
            this.connection.getClientId(),
            this.sdk.org,
            this.connection.fidjVersion,
            crypto
        );
    }

    public async fidjRemoveInDb(data_id: string): Promise<void | ErrorInterface> {
        this.logger.log('fidj.sdk.service.fidjRemoveInDb ', data_id);
        if (!this.sdk.useDB) {
            this.logger.log(
                'fidj.sdk.service.fidjRemoveInDb: you are not using DB - no remove available.'
            );
            return Promise.resolve();
        }

        if (!this.session.isReady()) {
            return this.promise.reject(new FidjError(400, 'Need to be synchronised.'));
        }

        if (!data_id || typeof data_id !== 'string') {
            return this.promise.reject(
                new FidjError(400, 'DB remove impossible. ' + 'Need the data._id.')
            );
        }

        return this.session.remove(data_id);
    }

    public async fidjFindInDb(data_id: string): Promise<any | ErrorInterface> {
        if (!this.sdk.useDB) {
            this.logger.log(
                'fidj.sdk.service.fidjFindInDb: you are not using DB - no find available.'
            );
            return Promise.resolve();
        }

        if (!this.connection.getClientId()) {
            return this.promise.reject(new FidjError(401, 'Find pb : need a user logged in.'));
        }
        if (!this.session.isReady()) {
            return this.promise.reject(new FidjError(400, ' Need to be synchronised.'));
        }

        let crypto: SessionCryptoInterface;
        if (this.connection.fidjCrypto) {
            crypto = {
                obj: this.connection,
                method: 'decrypt',
            };
        }

        return this.session.get(data_id, crypto);
    }

    public async fidjFindAllInDb(): Promise<Array<any> | ErrorInterface> {
        if (!this.sdk.useDB) {
            this.logger.log(
                'fidj.sdk.service.fidjFindAllInDb: you are not using DB - no find available.'
            );
            return Promise.resolve([]);
        }

        if (!this.connection.getClientId()) {
            return this.promise.reject(new FidjError(401, 'Need a user logged in.'));
        }
        if (!this.session.isReady()) {
            return this.promise.reject(new FidjError(400, 'Need to be synchronised.'));
        }

        let crypto: SessionCryptoInterface;
        if (this.connection.fidjCrypto) {
            crypto = {
                obj: this.connection,
                method: 'decrypt',
            };
        }

        return this.session.getAll(crypto).then((results) => {
            this.connection.setCryptoSaltAsVerified();
            return this.promise.resolve(results as Array<any>);
        });
    }

    public async sendOnEndpoint<TData = any, TResponse = any>(
        input: EndpointCallInterface<TData>
    ): Promise<{status: number; data?: TResponse}> {
        if (this.oidc()) {
            const base = input.defaultKeyUrl
                ? new URL(input.defaultKeyUrl).pathname.replace(/^\/v3/, '')
                : '/' + (input.key || 'me');
            return this.oidc().request(
                base + (input.relativePath ? '/' + input.relativePath : ''),
                input.verb,
                input.data
            );
        }
        await this.sync();

        const filter: EndpointFilterInterface = input.key ? {key: input.key} : null;
        const endpoints = await this.fidjGetEndpoints(filter);
        if (!input.defaultKeyUrl && (!endpoints || endpoints.length !== 1)) {
            throw new FidjError(400, 'fidj.sdk.service.sendOnEndpoint : endpoint does not exist.');
        }

        let firstEndpointUrl =
            !endpoints || endpoints.length !== 1 ? input.defaultKeyUrl : endpoints[0].url;
        if (input.relativePath) {
            firstEndpointUrl = new URL(
                input.relativePath.replace(/^\/+/, ''),
                firstEndpointUrl.replace(/\/?$/, '/')
            ).href;
        }
        const jwt = await this.connection.getIdToken();
        let answer: {status: number; data?: TResponse};
        const query = new Ajax();
        switch (input.verb) {
            case 'POST':
                answer = await query.post({
                    url: firstEndpointUrl,
                    withCredentials: input.withCredentials,
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + jwt,
                    },
                    data: input.data ? input.data : {},
                    timeout: input.timeout,
                });
                break;
            case 'PUT':
                answer = await query.put({
                    url: firstEndpointUrl,
                    withCredentials: input.withCredentials,
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + jwt,
                    },
                    data: input.data ? input.data : {},
                    timeout: input.timeout,
                });
                break;
            case 'DELETE':
                answer = await query.delete({
                    url: firstEndpointUrl,
                    withCredentials: input.withCredentials,
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + jwt,
                    },
                    timeout: input.timeout,
                    data: input.data,
                });
                break;
            default:
                answer = await query.get({
                    url: firstEndpointUrl,
                    withCredentials: input.withCredentials,
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + jwt,
                    },
                    timeout: input.timeout,
                    data: input.data,
                });
        }
        return answer;
    }

    // Typed API convenience methods (using @ofidj/contracts)

    public async getMe(): Promise<{status: number; data?: FidjApiUsersMeResponse}> {
        return this.sendOnEndpoint<void, FidjApiUsersMeResponse>({
            verb: 'GET',
            key: 'me',
        });
    }

    public async getMeDetails(): Promise<{status: number; data?: FidjApiUsersMeDetailsResponse}> {
        return this.sendOnEndpoint<void, FidjApiUsersMeDetailsResponse>({
            verb: 'GET',
            key: 'me',
            relativePath: 'details',
        });
    }

    public async updateMe(
        data: FidjApiUsersMeUpdateRequest
    ): Promise<{status: number; data?: FidjApiUsersMeResponse}> {
        return this.sendOnEndpoint<FidjApiUsersMeUpdateRequest, FidjApiUsersMeResponse>({
            verb: 'PUT',
            key: 'me',
            data,
        });
    }

    public async getConsents(): Promise<{status: number; data?: FidjApiConsentsResponse}> {
        return this.sendOnEndpoint<void, FidjApiConsentsResponse>({
            verb: 'GET',
            key: 'me',
            relativePath: 'consents',
        });
    }

    public async putConsents(
        data: FidjApiConsentsUpdateRequest
    ): Promise<{status: number; data?: FidjApiConsentsResponse}> {
        return this.sendOnEndpoint<FidjApiConsentsUpdateRequest, FidjApiConsentsResponse>({
            verb: 'PUT',
            key: 'me',
            relativePath: 'consents',
            data,
        });
    }

    public async getConsentsHistory(): Promise<{
        status: number;
        data?: FidjApiConsentsHistoryResponse;
    }> {
        return this.sendOnEndpoint<void, FidjApiConsentsHistoryResponse>({
            verb: 'GET',
            key: 'me',
            relativePath: 'consents/history',
        });
    }

    public async fidjForgotPasswordRequest(email: string): Promise<void> {
        await this.accountPost('/me/forgot', {email});
    }

    public async resetPassword(data: FidjApiResetPasswordRequest): Promise<void> {
        await this.accountPost('/me/reset-password', data);
        await this.logout(true);
    }

    public async verifyEmail(data: FidjApiVerifyEmailRequest): Promise<void> {
        await this.accountPost('/users/verify-email', data);
    }

    public async resendVerification(): Promise<void> {
        await this.sendOnEndpoint({verb: 'POST', key: 'me', relativePath: 'resend-verification'});
    }

    // Passkeys (v3 P1-4). The browser runs the WebAuthn ceremony with these
    // options; the SDK only carries the HTTP side, so it stays usable in Node.
    public async passkeyLoginOptions(): Promise<{options: any; ticket: string}> {
        const endpoints = await this.connection.getApiEndpoints({filter: 'theBestOne'});
        if (!endpoints || endpoints.length !== 1) {
            throw new FidjError(400, 'No configured account API endpoint.');
        }
        const answer: any = await new Ajax().post({
            url: endpoints[0].url.replace(/\/$/, '') + '/passkeys/options',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            data: {},
            timeout: FidjNodeService.DEFAULT_TIMEOUT_MS,
        });
        return answer.data;
    }

    // Signs in with the browser's answer to passkeyLoginOptions(), the way
    // login() does with a password: same session, same agreement rule.
    public async loginWithPasskey(
        ticket: string,
        response: any,
        options?: ModuleServiceLoginCallOptionsInterface
    ) {
        if (!this.connection.isReady()) {
            throw new FidjError(404, 'Need an initialized FidjService');
        }
        this.forgetProviderSession();
        await this._removeAll();
        await this._createSession(this.connection.fidjId);
        await this.connection.logout();
        const clientTokens = await this.connection
            .getClient()
            .loginWithPasskey(ticket, response, options);
        await this.connection.setConnection(clientTokens);
        if (this.sdk.useDB) {
            try {
                await this.session.sync(this.connection.getClientId());
            } catch (e) {
                this.logger.warn('fidj.sdk.service.loginWithPasskey: sync issue', e.toString());
            }
        }
        return this.connection.getUser();
    }

    private async accountPost(path: string, data: unknown): Promise<void> {
        const endpoints = await this.connection.getApiEndpoints({filter: 'theBestOne'});
        if (!endpoints || endpoints.length !== 1) {
            throw new FidjError(400, 'No configured account API endpoint.');
        }
        await new Ajax().post({
            url: endpoints[0].url.replace(/\/$/, '') + path,
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            data,
            timeout: FidjNodeService.DEFAULT_TIMEOUT_MS,
        });
    }

    public async fidjGetIdToken() {
        if (this.oidc()) {
            return this.oidc().accessToken();
        }
        return this.connection.getIdToken();
    }

    protected async _removeAll(): Promise<void | ErrorInterface> {
        await this.connection.destroy();
        await this.session.destroy();
    }

    private async _loginInternal(
        login: string,
        password: string,
        updateProperties?: any,
        options?: ModuleServiceLoginCallOptionsInterface
    ): Promise<ClientTokens> {
        this.logger.log('fidj.sdk.service._loginInternal');
        if (!this.connection.isReady()) {
            throw new FidjError(403, 'Need an initialized FidjService');
        }

        await this.connection.logout();

        return await this.connection.getClient().login(login, password, updateProperties, options);
    }

    private async _createSession(uid: string): Promise<void | ErrorInterface> {
        const dbs: EndpointInterface[] = await this.connection.getDBs({filter: 'theBestOnes'});
        if (!dbs || dbs.length === 0) {
            this.logger.warn('Seems that you are in Demo mode or using Node (no remote DB).');
        }
        this.session.setRemote(dbs);
        return this.session.create(uid);
    }

    private async _testPromise(a?): Promise<any> {
        if (a) {
            return this.promise.resolve('test promise ok ' + a);
        }
        return new this.promise((resolve, reject) => {
            resolve('test promise ok');
        });
    }

    private _generateObjectUniqueId(appName, type?, name?) {
        // return null;
        const now = new Date();
        const simpleDate =
            '' +
            now.getFullYear() +
            '' +
            now.getMonth() +
            '' +
            now.getDate() +
            '' +
            now.getHours() +
            '' +
            now.getMinutes(); // new Date().toISOString();
        const sequId = ++FidjNodeService._srvDataUniqId;
        let UId = '';
        if (appName && appName.charAt(0)) {
            UId += appName.charAt(0) + '';
        }
        if (type && type.length > 3) {
            UId += type.substring(0, 4);
        }
        if (name && name.length > 3) {
            UId += name.substring(0, 4);
        }
        UId += simpleDate + '' + sequId;
        return UId;
    }
}
